// src/app/services/router-state-tracker.service.ts
import { Injectable, OnDestroy, NgZone, ElementRef } from '@angular/core';
import { Router, NavigationEnd, Event as RouterEvent } from '@angular/router';
import { BehaviorSubject, Subscription, Subject, combineLatest } from 'rxjs';
import { filter } from 'rxjs/operators';
import { NavController } from '@ionic/angular';


@Injectable({
  providedIn: 'root',
})
export class RouterStateTrackerService implements OnDestroy {
  private _previousUrlSubject = new BehaviorSubject<string>('/');
  private _currentUrlSubject = new BehaviorSubject<string>('/');
  private _startUrlSubject = new BehaviorSubject<string>('/');
  private _activePageRef = new BehaviorSubject<any | null>(null);
  private routerEventSubscription?: Subscription;
  private _isNavigatingSubject = new BehaviorSubject<boolean>(false);
  isNavigating$ = this._isNavigatingSubject.asObservable();

  private _animationInProgress = false;
  
  stateData?: any;
  combinedData: { [key: string]: any } = {};
  constructor(
    private router: Router, 
    private navCtrl: NavController,
    private ngZone: NgZone,
  ) {
    this._currentUrlSubject.next(this.router.url.split('?')[0] || '/');
    this._startUrlSubject.next(this.router.url.split('?')[0] || '/');

    this.setupNavigationTracking();
  }

  /**
   * Tracks navigation events to update URL state
   */
  private setupNavigationTracking(): void {
    this.routerEventSubscription = this.router.events
      .pipe(filter((event: RouterEvent): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const newUrlPath = event.urlAfterRedirects.split('?')[0] || '/';
        this._previousUrlSubject.next(this._currentUrlSubject.getValue());
        this._currentUrlSubject.next(newUrlPath);
        this._animationInProgress = false;
      });
  }

  ngOnDestroy() {
    if (this.routerEventSubscription) {
      this.routerEventSubscription?.unsubscribe();
    }
  }

  registerActivePage(page: any): void {
    if (page) {
      const componentInstance = page as any;
      if (this._activePageRef.getValue() !== componentInstance) {
        this._activePageRef.next(componentInstance);
      }
    } else {
      if (this._activePageRef.getValue() !== null) {
        this._activePageRef.next(null);
      }
    }
  }

  unregisterActivePage(page: any): void {
    // Use `any` here
    if (this._activePageRef.getValue() === page) {
      this._activePageRef.next(null);
    }
  }
  updateCombinedData(key: string, newData: any): void {
    this.combinedData[key] = newData;
  }
  /**
   * Plays pre-exit animation for the currently active page
   */
  private async playPreExitAnimationForActivePage(): Promise<void> {
    const activePage = this._activePageRef.getValue();
    
    if (activePage && typeof activePage.playPreExitAnimation === 'function') {
      try {
        await activePage.playPreExitAnimation();
      } catch (error) {
        console.error('Pre-exit animation error:', error);
      }
    }
  }

  /**
   * Custom navigation method with pre-exit animation
   * This is the preferred method for programmatic navigation
   * Ensures smooth exit animation before navigating
   * Prevents navigation and animations if already on the target page
   */
  async navigateWithPreExitAnimation(url: string, pageData: any = {}, options?: any): Promise<boolean> {
    // Normalize URLs for comparison (remove leading/trailing slashes and query params)
    const currentUrl = this._currentUrlSubject.getValue().replace(/^\/+|\/+$/g, '').split('?')[0];
    const targetUrl = url.replace(/^\/+|\/+$/g, '').split('?')[0];
    
    // If already on the target page, do nothing - no animations, no navigation
    if (currentUrl === targetUrl) {
      // console.log(`Already on page: ${targetUrl}. Navigation and animations cancelled.`);
      return false;
    }
    
    if (this._isNavigatingSubject.getValue() || this._animationInProgress) {
      return false;
    }
    
    this.updateCombinedData(url, pageData);
    this._isNavigatingSubject.next(true);
    this._animationInProgress = true;

    try {
      // Play exit animation for current page
      await this.playPreExitAnimationForActivePage();
      
      // Perform navigation with NgZone for optimal performance
      this.stateData = options;
      const result = await this.ngZone.run(() => this.navCtrl.navigateRoot(url, options));
      return result;
    } catch (error) {
      console.error('Navigation error:', error);
      // Fallback: navigate without animation
      return await this.navCtrl.navigateRoot(url, options);
    } finally {
      this._isNavigatingSubject.next(false);
      // _animationInProgress is reset in NavigationEnd event
    }
  }

  /**
   * Wrapper for standard Angular Router navigation with animation
   * Use this instead of router.navigate() to ensure animations play
   */
  async navigateWithAnimation(commands: any[], extras?: any): Promise<boolean> {
    const url = commands.join('/');
    return this.navigateWithPreExitAnimation(url, {}, extras);
  }

  /**
   * Wrapper for Ionic back navigation with animation
   */
  async navigateBack(defaultUrl?: string): Promise<void> {
    if (this._animationInProgress) {
      return;
    }

    this._animationInProgress = true;
    
    try {
      await this.playPreExitAnimationForActivePage();
      await this.ngZone.run(() => this.navCtrl.back({ animated: false }));
    } catch (error) {
      console.error('Back navigation error:', error);
      if (defaultUrl) {
        await this.navCtrl.navigateRoot(defaultUrl);
      }
    }
  }

  setStartUrl(url: string): void {
    this._startUrlSubject.next(url.split('?')[0] || '/');
  }

  getTabUrl(): string {
    if (this._currentUrlSubject.getValue() === '/') {
      return this._startUrlSubject.getValue();
    }
    return this._currentUrlSubject.getValue();
  }

  getPreviousUrl(): string {
    return this._previousUrlSubject.getValue();
  }

  getCurrentUrl(): string {
    return this._currentUrlSubject.getValue();
  }
}
